import { useMemo } from "react"
import {
  useFetchIntegrationsQuery,
  useGetIntegrationDetailsQuery,
  useGetStatusQuery,
  useFetchBrandsQuery
} from "../../redux/queries"
import { Integration } from "../../types"

interface UseIntegrationsPageDataOpts {
  id?: string
  isSuperAdmin: boolean
  orgId?: string
  /** `""` = no API filter (all brands) */
  selectedBrandId?: string
}

function startOfTodayTimestamp(): number {
  const d = new Date()
  d.setHours(0, 0, 0, 0)
  return Math.floor(d.getTime() / 1000)
}

function sortByCreatedDesc(items: Integration[]): Integration[] {
  return [...items].sort((a, b) => {
    const aTs = a.created ? parseInt(a.created, 10) : 0
    const bTs = b.created ? parseInt(b.created, 10) : 0
    return bTs - aTs
  })
}

export function useIntegrationsPageData({
  id,
  isSuperAdmin,
  orgId,
  selectedBrandId
}: UseIntegrationsPageDataOpts) {
  const orgFilter = isSuperAdmin ? {} : { org_id: orgId }
  const todayStart = useMemo(() => startOfTodayTimestamp(), [])

  const listParams = useMemo(
    () => ({
      full_detail: true,
      limit: 999,
      ...orgFilter,
      ...(selectedBrandId ? { brand_id: selectedBrandId } : {})
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }),
    [isSuperAdmin, orgId, selectedBrandId]
  )

  const brandsQueryParams = useMemo(
    () => ({ limit: 999, ...orgFilter }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isSuperAdmin, orgId]
  )

  const { data: brands } = useFetchBrandsQuery(brandsQueryParams)

  const { data: listResponse, isLoading: isLoadingIntegrations } =
    useFetchIntegrationsQuery(listParams, { refetchOnMountOrArgChange: true })

  const allIntegrations = useMemo(
    () => sortByCreatedDesc(listResponse?.results ?? []),
    [listResponse]
  )

  const todayIntegrations = useMemo(
    () =>
      allIntegrations.filter(i => parseInt(i.created ?? "0", 10) >= todayStart),
    [allIntegrations, todayStart]
  )

  const historyIntegrations = useMemo(
    () =>
      allIntegrations.filter(i => parseInt(i.created ?? "0", 10) < todayStart),
    [allIntegrations, todayStart]
  )

  const { data: integration, isLoading: isLoadingIntegration } =
    useGetIntegrationDetailsQuery(id ?? "", { skip: !id })

  const { data: statusData = [], isLoading: isLoadingStatus } =
    useGetStatusQuery(
      { integrationID: integration?.id ?? "" },
      { skip: !integration?.id }
    )

  return {
    brands,
    todayIntegrations,
    integrations: historyIntegrations,
    integration,
    statusData,
    isLoadingIntegrations,
    isLoadingIntegration,
    isLoadingStatus
  }
}
